import type { PublicMatchMode } from '../config/contract';
import type { FinishUpdate } from '../progress/ProgressService';
import type { GameSettings, Medal, PhaseId, PlayerProfile } from '../progress/model';
import { PHASES, isUnlocked, lockReason, type PhaseDefinition } from '../tracks/phases';
import { esc, formatTime, medalLabel } from '../util/format';

export type MenuScreen =
  | 'boot'
  | 'title'
  | 'phases'
  | 'garage'
  | 'settings'
  | 'instructions'
  | 'loading'
  | 'paused'
  | 'results';

export interface UiHooks {
  onPlay: () => void;
  onPhases: () => void;
  onGarage: () => void;
  onSettings: () => void;
  onInstructions: () => void;
  onBack: () => void;
  onSelectPhase: (id: PhaseId) => void;
  onResume: () => void;
  onRestart: () => void;
  onQuit: () => void;
  onChangeSettings: (partial: Partial<GameSettings>) => void;
  onResetProgress: () => void;
  onReplay: () => void;
  onUnlockAdmin: (code: string) => void;
  onSetMatchMode: (mode: PublicMatchMode) => void;
}

export interface HudState {
  speed: number;
  lap: number;
  totalLaps: number;
  raceMs: number;
  lapMs: number;
  bestLapMs: number | null;
  bestRaceMs: number | null;
  position: number;
  grid: number;
  progress: number;
  countdown: string | null;
  message: string;
  collision: boolean;
  showHelp: boolean;
  boost: number;
  boosting: boolean;
  nitroFree: boolean;
}

interface PhaseView {
  phase: PhaseDefinition;
  unlocked: boolean;
  reason: string | null;
  result: PlayerProfile['phases'][PhaseId];
}

export class Ui {
  private readonly menu: HTMLElement;
  private readonly hud: HTMLElement;
  private readonly touch: HTMLElement;
  private screen: MenuScreen = 'boot';
  private stack: MenuScreen[] = [];
  private confirmReset = false;
  private profile: PlayerProfile | null = null;
  private persistence: 'local' | 'memory' | 'remote' = 'local';
  private degraded = false;
  private loadingText = 'Carregando…';
  private result: (FinishUpdate & { phaseName: string; nextName: string | null; epilogue?: string | null }) | null = null;
  private admin = false;
  private matchMode: PublicMatchMode = 'checkpointsAsNitro';
  private adminNote = '';

  constructor(
    root: HTMLElement,
    private readonly hooks: UiHooks,
    private readonly bindTouch: (button: HTMLElement, control: 'throttle' | 'brake' | 'left' | 'right' | 'handbrake' | 'boost') => void,
  ) {
    root.innerHTML = `
      <div id="menu"></div>
      <div id="hud" hidden>
        <div class="checkered"></div>
        <div class="hud-top">
          <div class="hud-card"><span>Volta</span><strong data-hud="lap">1/3</strong></div>
          <div class="hud-card hud-time">
            <strong data-hud="race">0:00.000</strong>
            <span data-hud="laptime">Volta 0:00.000</span>
          </div>
          <div class="hud-card"><span>Posição</span><strong data-hud="pos">1º</strong></div>
        </div>
        <div class="hud-message" data-hud="message"></div>
        <div class="countdown" data-hud="count" hidden></div>
        <div class="hud-bottom">
          <div class="speed"><strong data-hud="speed">0</strong><span>km/h</span></div>
          <div class="lap-progress"><span data-hud="bar"></span></div>
          <div class="best" data-hud="best">Melhor volta —</div>
        </div>
        <div class="mode-badge" data-hud="mode" hidden>Nitro livre</div>
        <div class="boost-meter" data-hud="boost-meter">
          <strong data-hud="boost">0</strong>
          <div class="boost-track"><span data-hud="boost-fill"></span></div>
          <span>Nitro</span>
        </div>
        <div class="help" data-hud="help">W acelerar · Shift nitro · A/D virar · Espaço freio de mão · R recuperar</div>
      </div>
      <div id="touch" hidden>
        <button type="button" data-touch="left" aria-label="Virar à esquerda">◀</button>
        <button type="button" data-touch="brake" aria-label="Frear">Freio</button>
        <button type="button" data-touch="handbrake" aria-label="Freio de mão">Mão</button>
        <button type="button" data-touch="right" aria-label="Virar à direita">▶</button>
        <button type="button" data-touch="boost" aria-label="Nitro">Nitro</button>
        <button type="button" class="gas" data-touch="throttle" aria-label="Acelerar">Acelerar</button>
      </div>
    `;
    this.menu = root.querySelector('#menu') as HTMLElement;
    this.hud = root.querySelector('#hud') as HTMLElement;
    this.touch = root.querySelector('#touch') as HTMLElement;
    this.touch.querySelectorAll<HTMLElement>('[data-touch]').forEach((button) => {
      const control = button.dataset.touch as 'throttle' | 'brake' | 'left' | 'right' | 'handbrake' | 'boost';
      this.bindTouch(button, control);
    });
    this.menu.addEventListener('click', this.onClick);
    this.menu.addEventListener('change', this.onChange);
  }

  setProfile(profile: PlayerProfile, persistence: 'local' | 'memory' | 'remote', degraded: boolean): void {
    this.profile = profile;
    this.persistence = persistence;
    this.degraded = degraded;
    if (this.screen !== 'boot') this.renderMenu();
  }

  show(screen: MenuScreen): void {
    this.stack = [];
    this.confirmReset = false;
    this.screen = screen;
    this.renderMenu();
  }

  push(screen: MenuScreen): void {
    this.stack.push(this.screen);
    this.confirmReset = false;
    this.screen = screen;
    this.renderMenu();
  }

  back(): void {
    this.confirmReset = false;
    this.screen = this.stack.pop() ?? 'title';
    this.renderMenu();
  }

  showLoading(text: string): void {
    this.loadingText = text;
    this.show('loading');
  }

  showResults(result: FinishUpdate & { phaseName: string; nextName: string | null; epilogue?: string | null }): void {
    this.result = result;
    this.show('results');
  }

  get current(): MenuScreen {
    return this.screen;
  }

  setHudVisible(visible: boolean): void {
    this.hud.hidden = !visible;
    const coarse = window.matchMedia('(pointer: coarse)').matches || window.innerWidth < 760;
    this.touch.hidden = !visible || !coarse;
  }

  updateHud(state: HudState): void {
    const set = (key: string, value: string) => {
      const node = this.hud.querySelector(`[data-hud="${key}"]`);
      if (node && node.textContent !== value) node.textContent = value;
    };
    set('lap', `${state.lap}/${state.totalLaps}`);
    set('race', formatTime(state.raceMs));
    set('laptime', `Volta ${formatTime(state.lapMs)}`);
    set('pos', `${state.position}º / ${state.grid}`);
    set('speed', String(state.speed));
    set('best', `Melhor volta ${formatTime(state.bestLapMs ?? state.bestRaceMs)}`);
    set('message', state.message);
    set('boost', String(Math.round(state.boost)));
    const bar = this.hud.querySelector<HTMLElement>('[data-hud="bar"]');
    if (bar) bar.style.width = `${Math.max(0, Math.min(1, state.progress)) * 100}%`;
    const fill = this.hud.querySelector<HTMLElement>('[data-hud="boost-fill"]');
    if (fill) fill.style.height = `${Math.max(0, Math.min(100, state.boost))}%`;
    this.hud.querySelector('[data-hud="boost-meter"]')?.classList.toggle('hot', state.boosting);
    const mode = this.hud.querySelector<HTMLElement>('[data-hud="mode"]');
    if (mode) mode.hidden = !state.nitroFree;
    const count = this.hud.querySelector<HTMLElement>('[data-hud="count"]');
    if (count) {
      count.hidden = !state.countdown;
      count.textContent = state.countdown ?? '';
    }
    const help = this.hud.querySelector<HTMLElement>('[data-hud="help"]');
    if (help) help.hidden = !state.showHelp;
    this.hud.classList.toggle('impact', state.collision);
  }

  private onClick = (event: Event): void => {
    const target = (event.target as HTMLElement).closest<HTMLElement>('[data-action]');
    if (!target) return;
    const action = target.dataset.action;
    const phase = target.dataset.phase as PhaseId | undefined;
    switch (action) {
      case 'play':
        this.hooks.onPlay();
        break;
      case 'phases':
        this.hooks.onPhases();
        break;
      case 'garage':
        this.hooks.onGarage();
        break;
      case 'settings':
        this.hooks.onSettings();
        break;
      case 'instructions':
        this.hooks.onInstructions();
        break;
      case 'back':
        this.hooks.onBack();
        break;
      case 'phase':
        if (phase) this.hooks.onSelectPhase(phase);
        break;
      case 'resume':
        this.hooks.onResume();
        break;
      case 'restart':
        this.hooks.onRestart();
        break;
      case 'quit':
        this.hooks.onQuit();
        break;
      case 'replay':
        this.hooks.onReplay();
        break;
      case 'reset-ask':
        this.confirmReset = true;
        this.renderMenu();
        break;
      case 'reset-cancel':
        this.confirmReset = false;
        this.renderMenu();
        break;
      case 'reset-confirm':
        this.confirmReset = false;
        this.hooks.onResetProgress();
        break;
      case 'admin-unlock': {
        const input = this.menu.querySelector<HTMLInputElement>('[data-setting="admin-code"]');
        this.hooks.onUnlockAdmin(input?.value ?? '');
        break;
      }
      default:
        break;
    }
  };

  private onChange = (event: Event): void => {
    const target = event.target as HTMLInputElement | HTMLSelectElement;
    if (target.dataset.setting === 'graphics') {
      this.hooks.onChangeSettings({ graphics: target.value === 'low' ? 'low' : 'high' });
    } else if (target.dataset.setting === 'volume') {
      this.hooks.onChangeSettings({ volume: Number(target.value) / 100 });
    } else if (target.dataset.setting === 'camera') {
      this.hooks.onChangeSettings({ camera: target.value === 'high' ? 'high' : 'chase' });
    } else if (target.dataset.setting === 'shake') {
      this.hooks.onChangeSettings({ cameraShake: (target as HTMLInputElement).checked });
    } else if (target.dataset.setting === 'bounce') {
      this.hooks.onChangeSettings({ frontBounce: (target as HTMLInputElement).checked });
    } else if (target.dataset.setting === 'match-mode') {
      this.hooks.onSetMatchMode(target.value === 'nitroSpawnersOnly' ? 'nitroSpawnersOnly' : 'checkpointsAsNitro');
    }
  };

  setMatchAdmin(admin: boolean, mode: PublicMatchMode, note = ''): void {
    this.admin = admin;
    this.matchMode = mode;
    this.adminNote = note;
    if (this.screen === 'settings') this.renderMenu();
  }

  private phaseViews(): PhaseView[] {
    const profile = this.profile;
    const completed = (id: PhaseId) => profile?.phases[id]?.completed === true;
    return PHASES.map((phase) => ({
      phase,
      unlocked: isUnlocked(phase, completed),
      reason: lockReason(phase, completed),
      result: profile?.phases[phase.id] ?? {
        phaseId: phase.id,
        completed: false,
        bestTimeMs: null,
        bestLapMs: null,
        medal: 'none' as Medal,
        attempts: 0,
        finishes: 0,
        lastPlayedAt: null,
      },
    }));
  }

  private renderMenu(): void {
    this.menu.hidden = this.screen === 'boot';
    this.menu.className = `screen screen-${this.screen}`;
    this.menu.innerHTML = this.markup();
  }

  private markup(): string {
    switch (this.screen) {
      case 'boot':
        return '';
      case 'loading':
        return `<section class="panel"><p class="loading">${esc(this.loadingText)}</p></section>`;
      case 'title':
        return this.titleMarkup();
      case 'phases':
        return this.phasesMarkup();
      case 'garage':
        return this.garageMarkup();
      case 'settings':
        return this.settingsMarkup();
      case 'instructions':
        return this.instructionsMarkup();
      case 'paused':
        return this.pauseMarkup();
      case 'results':
        return this.resultsMarkup();
      default:
        return '';
    }
  }

  private adminMarkup(): string {
    const note = this.adminNote ? `<p class="warn">${esc(this.adminNote)}</p>` : '';
    if (!this.admin) {
      return `
        <fieldset class="admin-box">
          <legend>Partida pública</legend>
          <p>Somente administradores mudam o modo da partida pública.</p>
          <label>Código de admin
            <input data-setting="admin-code" type="password" autocomplete="off" />
          </label>
          <button type="button" data-action="admin-unlock">Entrar como admin</button>
          ${note}
        </fieldset>
      `;
    }
    const checkpoints = this.matchMode === 'checkpointsAsNitro';
    return `
      <fieldset class="admin-box">
        <legend>Partida pública</legend>
        <label>Modo da partida
          <select data-setting="match-mode">
            <option value="checkpointsAsNitro" ${checkpoints ? 'selected' : ''}>Checkpoints + nitro</option>
            <option value="nitroSpawnersOnly" ${checkpoints ? '' : 'selected'}>Só spawners de nitro</option>
          </select>
        </label>
        <p class="note">Checkpoints + nitro: cada grupo de 5 avança a volta e enche o tanque. Só spawners: os pads só dão nitro e a volta conta pela distância, sem setor obrigatório.</p>
        ${note}
      </fieldset>
    `;
  }

  private saveNote(): string {
    if (this.degraded || this.persistence === 'memory') {
      return '<p class="warn">Não foi possível usar o armazenamento deste navegador. O progresso vale só nesta sessão.</p>';
    }
    return '<p class="note">Progresso salvo neste navegador.</p>';
  }

  private titleMarkup(): string {
    return `
      <section class="panel title-panel">
        <p class="eyebrow">MemoCart apresenta</p>
        <h1><span>Supermarket</span> Kart Racing</h1>
        <p class="lede">Um carrinho de supermercado. Sete pistas. Nitro nos setores e derrapagem na curva.</p>
        <div class="actions">
          <button type="button" data-action="play">Jogar</button>
          <button type="button" data-action="phases">Selecionar fase</button>
          <button type="button" data-action="garage">Garagem</button>
          <button type="button" data-action="settings">Configurações</button>
          <button type="button" data-action="instructions">Instruções</button>
        </div>
        ${this.saveNote()}
      </section>
    `;
  }

  private phasesMarkup(): string {
    const cards = this.phaseViews().map(({ phase, reason, result }) => {
      const locked = reason != null;
      const medal = result.medal === 'none' ? '' : `<em class="medal medal-${result.medal}">${esc(medalLabel(result.medal))}</em>`;
      return `
        <article class="phase-card ${locked ? 'locked' : ''}">
          <div class="order">${phase.order}</div>
          <div>
            <h2>${esc(phase.name)} ${medal}</h2>
            <p>${esc(phase.summary)}</p>
            <p class="meta">${locked ? esc(reason ?? '') : `Melhor ${formatTime(result.bestTimeMs)} · ${phase.laps} voltas`}</p>
          </div>
          ${locked ? '' : `<button type="button" data-action="phase" data-phase="${phase.id}">Correr</button>`}
        </article>
      `;
    }).join('');
    return `
      <section class="panel wide">
        <header class="panel-head"><h1>Selecionar fase</h1><button type="button" data-action="back">Voltar</button></header>
        <div class="phase-list">${cards}</div>
      </section>
    `;
  }

  private garageMarkup(): string {
    const rows = this.phaseViews().map(({ phase, result }) => `
      <li>
        <span>${esc(phase.name)}</span>
        <span class="medal medal-${result.medal}">${esc(medalLabel(result.medal))}</span>
        <span>${formatTime(result.bestTimeMs)}</span>
      </li>
    `).join('');
    return `
      <section class="panel">
        <header class="panel-head"><h1>Garagem</h1><button type="button" data-action="back">Voltar</button></header>
        <p>Carrinho regulamentar: cesta de arame cromado, alça e para-choques vermelhos, quadro tubular e quatro rodízios. Não é um kart.</p>
        <p>O módulo fictício de propulsão só entra na fase Espaço, para explicar como o mesmo carrinho permanece na estrada.</p>
        <ul class="garage-list">${rows}</ul>
      </section>
    `;
  }

  private settingsMarkup(): string {
    const settings = this.profile?.settings;
    const graphics = settings?.graphics ?? 'high';
    const volume = Math.round((settings?.volume ?? 0.7) * 100);
    const camera = settings?.camera ?? 'chase';
    const shake = settings?.cameraShake !== false;
    const bounce = settings?.frontBounce === true;
    const reset = this.confirmReset
      ? `<div class="confirm"><p>Apagar tempos, medalhas e fases desbloqueadas?</p><button type="button" data-action="reset-confirm">Confirmar exclusão</button><button type="button" data-action="reset-cancel">Cancelar</button></div>`
      : `<button type="button" data-action="reset-ask">Reiniciar progresso</button>`;
    return `
      <section class="panel">
        <header class="panel-head"><h1>Configurações</h1><button type="button" data-action="back">Voltar</button></header>
        <label>Qualidade gráfica
          <select data-setting="graphics">
            <option value="high" ${graphics === 'high' ? 'selected' : ''}>Completo</option>
            <option value="low" ${graphics === 'low' ? 'selected' : ''}>Baixo</option>
          </select>
        </label>
        <label>Volume <input data-setting="volume" type="range" min="0" max="100" value="${volume}" /></label>
        <label>Câmera
          <select data-setting="camera">
            <option value="chase" ${camera === 'chase' ? 'selected' : ''}>Perseguição</option>
            <option value="high" ${camera === 'high' ? 'selected' : ''}>Alta</option>
          </select>
        </label>
        <label class="check"><input data-setting="shake" type="checkbox" ${shake ? 'checked' : ''} /> Vibração da câmera</label>
        <label class="check"><input data-setting="bounce" type="checkbox" ${bounce ? 'checked' : ''} /> Balanço na frente</label>
        <p class="note">O balanço só mexe o nariz do carrinho ao frear, acelerar ou pousar. O chassi continua rígido.</p>
        ${this.adminMarkup()}
        <p class="note">Alguns detalhes do cenário entram na próxima corrida. A fase carrega sob demanda.</p>
        ${reset}
        ${this.saveNote()}
      </section>
    `;
  }

  private instructionsMarkup(): string {
    return `
      <section class="panel">
        <header class="panel-head"><h1>Instruções</h1><button type="button" data-action="back">Voltar</button></header>
        <ul class="keys">
          <li><kbd>W</kbd> <kbd>↑</kbd> Acelerar</li>
          <li><kbd>S</kbd> <kbd>↓</kbd> Frear e, parado, dar ré</li>
          <li><kbd>A</kbd> <kbd>D</kbd> ou <kbd>←</kbd> <kbd>→</kbd> Virar</li>
          <li><kbd>Espaço</kbd> Freio de mão</li>
          <li><kbd>Shift</kbd> Nitro</li>
          <li><kbd>R</kbd> Recuperar a posição</li>
          <li><kbd>Esc</kbd> Pausar</li>
          <li><kbd>C</kbd> Alternar a câmera</li>
        </ul>
        <p>No celular, use os botões na parte de baixo. Um controle padrão também funciona: gatilhos aceleram e freiam, o botão de baixo é o freio de mão e o botão da direita (B) é o nitro.</p>
        <p>Cada volta só conta se você passar pelos setores na ordem, no modo checkpoints + nitro. Os setores vêm em grupos de 5 e enchem o tanque. No modo só spawners, os mesmos pads só dão nitro e a volta conta pelo percurso. Medalhas dependem do tempo total. Concluir uma fase libera a próxima.</p>
        <p>Se a janela perder o foco, o acelerador solta. A física é arcade: massa, aderência e derrapagem, sem pretender simular um veículo real.</p>
      </section>
    `;
  }

  private pauseMarkup(): string {
    return `
      <section class="panel">
        <h1>Pausado</h1>
        <div class="actions">
          <button type="button" data-action="resume">Continuar</button>
          <button type="button" data-action="restart">Reiniciar corrida</button>
          <button type="button" data-action="settings">Configurações</button>
          <button type="button" data-action="quit">Voltar ao menu</button>
        </div>
      </section>
    `;
  }

  private resultsMarkup(): string {
    const result = this.result;
    if (!result) return '';
    const next = result.nextName ? `<p>Próxima fase: ${esc(result.nextName)}.</p>` : '';
    const epilogue = result.epilogue ? `<p>${esc(result.epilogue)}</p>` : '';
    return `
      <section class="panel">
        <p class="eyebrow">Chegada</p>
        <h1>${esc(result.phaseName)}</h1>
        <p class="time-hero">${formatTime(result.totalMs)}</p>
        <p>Medalha <strong class="medal medal-${result.medal}">${esc(medalLabel(result.medal))}</strong>${result.isNewBest ? ' · novo recorde' : ''}</p>
        <p>Melhor volta ${formatTime(result.bestLapMs)} · recorde da fase ${formatTime(result.bestTimeMs)}</p>
        ${epilogue}
        ${next}
        <div class="actions">
          <button type="button" data-action="replay">Repetir</button>
          <button type="button" data-action="phases">Selecionar fase</button>
          <button type="button" data-action="quit">Menu</button>
        </div>
      </section>
    `;
  }
}
